import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import VoiceSelector from "../VoiceSelector";
import ScriptActions from "Redux/ScriptRedux";
import { CreditSelectors } from "Redux/CreditRedux";
import { calculateEstimatedCost } from "../credits/CreditCostEstimate";

// Rotating placeholder examples for the topic input
const PLACEHOLDER_EXAMPLES = [
  "10 facts about space that will blow your mind",
  "Morning routine tips for a productive day",
  "Budget travel hacks for Europe in 2026",
  "How artificial intelligence is changing healthcare",
  "5 simple recipes anyone can cook in 15 minutes",
  "The psychology behind procrastination and how to beat it",
  "A beginner's guide to investing in your 20s",
];

// Trending topic pills
const TRENDING_TOPICS = [
  { label: "10 facts about space", topic: "10 mind-blowing facts about space that most people don't know" },
  { label: "Morning routine tips", topic: "5 morning routine tips for a productive and energized day" },
  { label: "Budget travel hacks", topic: "Budget travel hacks for seeing the world without going broke" },
  { label: "AI explained simply", topic: "How artificial intelligence works, explained simply for beginners" },
  { label: "Motivational speech", topic: "A powerful 60-second motivational speech about never giving up" },
  { label: "Cooking in 15 mins", topic: "3 delicious meals anyone can cook in under 15 minutes" },
];

// Aspect ratio options
const ASPECT_RATIOS = [
  { id: "9:16", label: "9:16", subtitle: "Shorts" },
  { id: "16:9", label: "16:9", subtitle: "Landscape" },
  { id: "1:1", label: "1:1", subtitle: "Square" },
];

// Duration slider steps
const DURATION_STEPS = [
  { label: "15s", value: 15 },
  { label: "30s", value: 30 },
  { label: "45s", value: 45 },
  { label: "60s", value: 60 },
  { label: "90s", value: 90 },
  { label: "2m", value: 120 },
  { label: "3m", value: 180 },
];

// Content style options
const CONTENT_STYLES = [
  { id: "informative", label: "Informative", description: "Clear & factual", iconBg: "bg-blue-100", iconColor: "text-blue-600" },
  { id: "storytelling", label: "Storytelling", description: "Narrative driven", iconBg: "bg-green-100", iconColor: "text-green-600" },
  { id: "fun", label: "Fun & Light", description: "Casual tone", iconBg: "bg-purple-100", iconColor: "text-purple-600" },
  { id: "professional", label: "Professional", description: "Business focused", iconBg: "bg-slate-100", iconColor: "text-slate-600" },
  { id: "educational", label: "Educational", description: "Step-by-step learning", iconBg: "bg-amber-100", iconColor: "text-amber-600" },
  { id: "calm", label: "Calm & Sleepy", description: "Soothing narration", iconBg: "bg-teal-100", iconColor: "text-teal-600" },
];

// Video type options
const VIDEO_TYPES = [
  { id: "short", label: "YouTube Short", icon: "⚡" },
  { id: "long", label: "Long-form Video", icon: "🎬" },
];

// Image frequency options
const IMAGE_FREQUENCIES = [
  { id: 3, label: "Every 3 seconds" },
  { id: 5, label: "Every 5 seconds" },
  { id: 10, label: "Every 10 seconds" },
  { id: 15, label: "Every 15 seconds" },
  { id: 0, label: "One image for entire video" },
];

// Animation scope options
const ANIMATION_SCOPES = [
  { id: "all", label: "Entire video", description: "Animate all segments" },
  { id: "first_segment", label: "First segment only", description: "Cost-effective option" },
];

// Predefined image style options
const IMAGE_STYLES = [
  { id: "default", label: "Default", description: "Clean, modern animated style" },
  { id: "cinematic", label: "Cinematic", description: "Film-like visuals with dramatic lighting and depth of field" },
  { id: "anime", label: "Anime", description: "Japanese animation style with vibrant colors and expressive characters" },
  { id: "3d-render", label: "3D Render", description: "Polished 3D graphics with realistic lighting and textures" },
  { id: "flat-vector", label: "Flat Vector", description: "Bold flat colors, simple geometric shapes, no gradients, modern 2D illustration" },
  { id: "kids-cartoon", label: "Kids Cartoon", description: "Playful, colorful 2D cartoon style for children's content with bright colors and simple shapes" },
  { id: "motion-graphics", label: "Motion Graphics", description: "Clean vector graphics with bold colors, perfect for explainer videos and infographics" },
  { id: "watercolor", label: "Watercolor", description: "Soft, flowing watercolor painting with gentle color blends" },
  { id: "oil-painting", label: "Oil Painting", description: "Rich, textured oil painting style with bold brushstrokes" },
  { id: "digital-art", label: "Digital Art", description: "Modern digital illustration with clean lines and vibrant colors" },
  { id: "minimalist", label: "Minimalist", description: "Simple, clean designs with limited colors and shapes" },
  { id: "vintage", label: "Vintage", description: "Retro aesthetic with muted colors and film grain" },
  { id: "neon", label: "Neon Cyberpunk", description: "Futuristic neon-lit scenes with dark backgrounds" },
  { id: "fantasy", label: "Fantasy Art", description: "Magical, ethereal scenes with mystical elements" },
  { id: "comic", label: "Comic Book", description: "Bold outlines, halftone dots, and dynamic action poses" },
  { id: "pixel-art", label: "Pixel Art", description: "Retro 8-bit or 16-bit style pixel graphics" },
  { id: "photorealistic", label: "Photorealistic", description: "Hyper-realistic images that look like photographs" },
  { id: "sketch", label: "Pencil Sketch", description: "Hand-drawn pencil sketch with shading and texture" },
  { id: "pastel", label: "Soft Pastel", description: "Gentle pastel colors with dreamy, soft aesthetics" },
  { id: "pop-art", label: "Pop Art", description: "Bold colors, patterns, and Andy Warhol-inspired style" },
  { id: "isometric", label: "Isometric", description: "3D isometric perspective with clean geometric shapes" },
  { id: "low-poly", label: "Low Poly", description: "Geometric low-polygon 3D art style" },
  { id: "storybook", label: "Storybook", description: "Whimsical children's book illustration style" },
  { id: "noir", label: "Film Noir", description: "High contrast black and white with dramatic shadows" },
  { id: "surreal", label: "Surrealist", description: "Dreamlike, abstract imagery with unexpected combinations" },
  { id: "nature-doc", label: "Nature Documentary", description: "Realistic nature scenes with cinematic quality" },
  { id: "corporate", label: "Corporate Clean", description: "Professional, polished visuals for business content" },
  { id: "hand-drawn", label: "Hand Drawn", description: "Organic, imperfect hand-illustrated style" },
  { id: "gradient", label: "Gradient Abstract", description: "Smooth color gradients with abstract shapes" },
  { id: "vaporwave", label: "Vaporwave", description: "80s/90s aesthetic with pink, purple, and cyan colors" },
  { id: "gothic", label: "Gothic Dark", description: "Dark, moody atmosphere with ornate details" },
  { id: "kawaii", label: "Kawaii Cute", description: "Adorable Japanese cute style with pastel colors" },
  { id: "paper-cut", label: "Paper Cut", description: "Layered paper cutout style with depth and shadows" },
  { id: "claymation", label: "Claymation", description: "Stop-motion clay animation look with textured surfaces" },
  { id: "line-art", label: "Line Art", description: "Clean continuous line drawings with minimal fills" },
  { id: "retro-cartoon", label: "Retro Cartoon", description: "Classic 1950s-60s cartoon style with limited colors" },
  { id: "sci-fi", label: "Sci-Fi", description: "Futuristic technology and space themes with sleek designs" },
  { id: "steampunk", label: "Steampunk", description: "Victorian-era industrial aesthetic with brass and gears" },
  { id: "custom", label: "Custom Style", description: "Write your own custom style description" },
];

// Predefined theme color palettes
const THEME_COLORS = [
  { id: "none", label: "Auto", colors: [], description: "Let AI choose colors" },
  { id: "warm", label: "Warm", colors: ["#FF6B6B", "#FFA07A", "#FFD93D"], description: "Reds, oranges, yellows" },
  { id: "cool", label: "Cool", colors: ["#4ECDC4", "#45B7D1", "#96CEB4"], description: "Blues, teals, greens" },
  { id: "pastel", label: "Pastel", colors: ["#FFB5E8", "#B5DEFF", "#BFFCC6"], description: "Soft, muted tones" },
  { id: "earth", label: "Earth", colors: ["#8B7355", "#6B8E23", "#D2691E"], description: "Browns, greens, naturals" },
  { id: "ocean", label: "Ocean", colors: ["#006994", "#40E0D0", "#87CEEB"], description: "Deep blues, aquas" },
  { id: "sunset", label: "Sunset", colors: ["#FF6B6B", "#FF8E53", "#FEC89A"], description: "Orange, pink, coral" },
  { id: "forest", label: "Forest", colors: ["#228B22", "#2E8B57", "#556B2F"], description: "Deep greens, moss" },
  { id: "monochrome", label: "Monochrome", colors: ["#2C3E50", "#7F8C8D", "#BDC3C7"], description: "Grays, blacks, whites" },
  { id: "neon", label: "Neon", colors: ["#FF00FF", "#00FFFF", "#39FF14"], description: "Bright, electric colors" },
  { id: "vintage", label: "Vintage", colors: ["#D4A574", "#C9B896", "#A0785A"], description: "Sepia, cream, brown" },
  { id: "royal", label: "Royal", colors: ["#4B0082", "#800020", "#FFD700"], description: "Purple, burgundy, gold" },
  { id: "candy", label: "Candy", colors: ["#FF69B4", "#87CEEB", "#98FB98"], description: "Bright pinks, blues" },
  { id: "midnight", label: "Midnight", colors: ["#191970", "#483D8B", "#4B0082"], description: "Deep blues, purples" },
  { id: "custom", label: "Custom", colors: [], description: "Enter your own colors" },
];

// Predefined video/animation style options
const VIDEO_STYLES = [
  { id: "default", label: "Default", description: "Natural, smooth motion with subtle camera movement" },
  { id: "cinematic", label: "Cinematic", description: "Film-like motion with dramatic camera pans and depth of field" },
  { id: "slow-motion", label: "Slow Motion", description: "Graceful, slowed-down movement for dramatic effect" },
  { id: "timelapse", label: "Timelapse", description: "Accelerated motion showing passage of time" },
  { id: "parallax", label: "Parallax", description: "Layered depth effect with foreground/background movement" },
  { id: "zoom-in", label: "Zoom In", description: "Gradual zoom towards the subject" },
  { id: "zoom-out", label: "Zoom Out", description: "Gradual zoom away revealing more of the scene" },
  { id: "pan-left", label: "Pan Left", description: "Smooth horizontal camera movement to the left" },
  { id: "pan-right", label: "Pan Right", description: "Smooth horizontal camera movement to the right" },
  { id: "tilt-up", label: "Tilt Up", description: "Vertical camera movement upward" },
  { id: "tilt-down", label: "Tilt Down", description: "Vertical camera movement downward" },
  { id: "orbit", label: "Orbit", description: "Camera circles around the subject" },
  { id: "dolly", label: "Dolly", description: "Camera moves toward or away from subject" },
  { id: "handheld", label: "Handheld", description: "Subtle shake for documentary-style realism" },
  { id: "floating", label: "Floating", description: "Gentle, dreamy floating motion" },
  { id: "breathing", label: "Breathing", description: "Subtle pulsing motion like breathing" },
  { id: "particle", label: "Particle Effects", description: "Add floating particles, dust, or sparkles" },
  { id: "wind", label: "Wind Effect", description: "Elements sway as if blown by wind" },
  { id: "water", label: "Water Ripple", description: "Gentle water-like rippling motion" },
  { id: "glitch", label: "Glitch", description: "Digital glitch effects and distortions" },
  { id: "vhs", label: "VHS/Retro", description: "Vintage video tape aesthetic with scan lines" },
  { id: "film-grain", label: "Film Grain", description: "Classic film grain texture overlay" },
  { id: "bokeh", label: "Bokeh", description: "Soft focus background with light blur effects" },
  { id: "dramatic", label: "Dramatic", description: "Bold, intense movements with high contrast" },
  { id: "subtle", label: "Subtle", description: "Minimal, barely perceptible motion" },
  { id: "energetic", label: "Energetic", description: "Fast, dynamic movements with quick cuts" },
  { id: "calm", label: "Calm", description: "Slow, peaceful, meditative motion" },
  { id: "loop", label: "Seamless Loop", description: "Motion designed to loop perfectly" },
  { id: "3d-depth", label: "3D Depth", description: "Enhanced 3D depth perception effect" },
  { id: "ken-burns", label: "Ken Burns", description: "Classic documentary-style slow pan and zoom on still images" },
  { id: "bounce", label: "Bounce", description: "Playful bouncing motion with elastic feel" },
  { id: "shake", label: "Shake", description: "Intense shaking effect for action or impact moments" },
  { id: "rotate", label: "Rotate", description: "Smooth rotation around the center axis" },
  { id: "morph", label: "Morph", description: "Elements smoothly transform and morph into each other" },
  { id: "reveal", label: "Reveal", description: "Elements gradually appear with wipe or fade effects" },
  { id: "pulse", label: "Pulse", description: "Rhythmic pulsing synchronized with audio beats" },
  { id: "wave", label: "Wave", description: "Flowing wave-like motion across the scene" },
  { id: "flicker", label: "Flicker", description: "Light flickering effect like candlelight or old film" },
  { id: "strobe", label: "Strobe", description: "Quick flashing strobe light effect" },
  { id: "split-screen", label: "Split Screen", description: "Dynamic split-screen transitions and movements" },
  { id: "liquid", label: "Liquid", description: "Fluid, liquid-like motion and transitions" },
  { id: "elastic", label: "Elastic", description: "Stretchy, rubbery motion with overshoot" },
  { id: "smoke", label: "Smoke/Fog", description: "Atmospheric smoke or fog drifting through scene" },
  { id: "fire", label: "Fire", description: "Flickering fire and flame effects" },
  { id: "rain", label: "Rain", description: "Falling rain drops with atmospheric effect" },
  { id: "snow", label: "Snow", description: "Gentle falling snowflakes" },
  { id: "sparkle", label: "Sparkle", description: "Twinkling sparkle and shimmer effects" },
  { id: "neon-glow", label: "Neon Glow", description: "Pulsing neon light glow effects" },
  { id: "comic-action", label: "Comic Action", description: "Comic book style action lines and impact frames" },
  { id: "anime-speed", label: "Anime Speed", description: "Anime-style speed lines and dramatic zooms" },
  { id: "custom", label: "Custom Style", description: "Write your own animation style description" },
];

// Content style SVG icons
function StyleIcon({ styleId, className = "w-4 h-4" }) {
  switch (styleId) {
    case "informative":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 20 20">
          <rect x="2" y="10" width="4" height="8" rx="1" />
          <rect x="8" y="6" width="4" height="12" rx="1" />
          <rect x="14" y="2" width="4" height="16" rx="1" />
        </svg>
      );
    case "storytelling":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 20 20">
          <rect x="2" y="2" width="7" height="7" rx="1.5" />
          <rect x="11" y="2" width="7" height="7" rx="1.5" />
          <rect x="2" y="11" width="7" height="7" rx="1.5" />
          <rect x="11" y="11" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "fun":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" />
        </svg>
      );
    case "professional":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm-2 7.47V16a2 2 0 002 2h4a2 2 0 002-2v-3.53a24.977 24.977 0 01-4 .5 24.977 24.977 0 01-4-.5z" clipRule="evenodd" />
        </svg>
      );
    case "educational":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 20 20">
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.547l1.607.688a3 3 0 002.786 0l1.607-.688v3.547a9.026 9.026 0 00-2.3 1.638z" />
        </svg>
      );
    case "calm":
      return (
        <svg className={className} fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      );
    default:
      return null;
  }
}

export default function TopicStep({ 
  activeTab,
  onTabChange,
  topic, 
  onTopicChange, 
  script,
  onScriptChange,
  wordCount,
  settings,
  onSettingsChange,
  onGenerateScript,
  onGenerateFromScript,
  isLoading 
}) {
  const dispatch = useDispatch();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [characterImagePreview, setCharacterImagePreview] = useState(null);
  const [selectedStyleId, setSelectedStyleId] = useState("default");
  const [showCustomStyle, setShowCustomStyle] = useState(false);
  const [selectedThemeColorId, setSelectedThemeColorId] = useState("none");
  const [customThemeColor, setCustomThemeColor] = useState("");
  const [selectedVideoStyleId, setSelectedVideoStyleId] = useState("default");
  const [showCustomVideoStyle, setShowCustomVideoStyle] = useState(false);
  const [isSurpriseLoading, setIsSurpriseLoading] = useState(false);
  const [promptIdeas, setPromptIdeas] = useState([]);
  const [isLoadingIdeas, setIsLoadingIdeas] = useState(false);
  const [showHookGenerator, setShowHookGenerator] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  // Rotate placeholder text
  useEffect(() => {
    if (activeTab !== "idea" || topic) return;
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % PLACEHOLDER_EXAMPLES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [activeTab, topic]);
  const [hookTopic, setHookTopic] = useState("");
  const [hookPlatform, setHookPlatform] = useState("tiktok");
  const [hooks, setHooks] = useState([]);
  const [isLoadingHooks, setIsLoadingHooks] = useState(false);
  const fileInputRef = useRef(null);

  // Credit estimate for bottom bar
  const costs = useSelector(CreditSelectors.getCosts);

  const estimate = useMemo(() => {
    return calculateEstimatedCost(settings, costs);
  }, [settings, costs]);

  // Duration slider index (derived from settings)
  const durationIndex = useMemo(() => {
    const idx = DURATION_STEPS.findIndex(s => s.value === settings.duration);
    return idx >= 0 ? idx : 2; // Default to 45s
  }, [settings.duration]);

  const updateSetting = (key, value) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  const handleCharacterImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file (JPG, PNG)");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert("Image size must be less than 10MB");
        return;
      }
      
      onSettingsChange({ 
        ...settings, 
        characterImage: file, 
        useCharacterReference: true 
      });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setCharacterImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCharacterImage = () => {
    onSettingsChange({ 
      ...settings, 
      characterImage: null, 
      useCharacterReference: false,
      characterLabel: ""
    });
    setCharacterImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDurationSliderChange = (e) => {
    const index = parseInt(e.target.value);
    updateSetting("duration", DURATION_STEPS[index].value);
  };

  const handleSurpriseMe = () => {
    setIsSurpriseLoading(true);
    dispatch(ScriptActions.scriptSurpriseRequest((data, error) => {
      setIsSurpriseLoading(false);
      if (error) {
        console.error("Surprise script failed:", error);
        return;
      }
      if (data?.content) {
        onScriptChange(data.content);
      }
    }));
  };

  const loadPromptIdeas = () => {
    setIsLoadingIdeas(true);
    dispatch(ScriptActions.scriptPromptIdeasRequest({ count: 6 }, (data, error) => {
      setIsLoadingIdeas(false);
      if (error) {
        console.error("Failed to load prompt ideas:", error);
        return;
      }
      setPromptIdeas(data?.ideas || []);
    }));
  };

  const loadHooks = () => {
    if (!hookTopic.trim()) return;
    setIsLoadingHooks(true);
    dispatch(ScriptActions.scriptGenerateHooksRequest(
      { topic: hookTopic.trim(), platform: hookPlatform, count: 8 },
      (data, error) => {
        setIsLoadingHooks(false);
        if (data?.hooks) setHooks(data.hooks);
      }
    ));
  };

  useEffect(() => {
    loadPromptIdeas();
  }, []);

  const handleSelectIdea = (idea) => {
    onTopicChange(idea.title);
    onScriptChange(idea.description);
    onSettingsChange({
      ...settings,
      ...(idea.content_style ? { contentStyle: idea.content_style } : {}),
      ...(idea.suggested_duration ? { duration: idea.suggested_duration } : {}),
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-36 sm:pb-32">

        {/* ── Tab Selector ── */}
        <div className="inline-flex bg-gray-100 rounded-xl p-1 mb-8">
          <button
            onClick={() => onTabChange("idea")}
            disabled={isLoading}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === "idea"
                ? "bg-primary-500 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            } disabled:opacity-50`}
          >
            <span>💡</span> Idea to Video
          </button>
          <button
            onClick={() => onTabChange("script")}
            disabled={isLoading}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === "script"
                ? "bg-primary-500 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-800"
            } disabled:opacity-50`}
          >
            <span>📄</span> Script to Video
          </button>
        </div>

        {/* ── Topic/Script Input ── */}
        <div className="mb-8">
          <label className="block text-base font-semibold text-gray-900 mb-3">
            {activeTab === "idea" ? "What is your video about?" : "Enter your script"}
          </label>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent transition-all">
            <textarea
              value={activeTab === "idea" ? topic : script}
              onChange={(e) => {
                if (e.target.value.length <= 10000) {
                  if (activeTab === "idea") {
                    onTopicChange(e.target.value);
                  } else {
                    onScriptChange(e.target.value);
                  }
                }
              }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !isLoading) {
                  e.preventDefault();
                  if (activeTab === "idea" && topic.trim()) onGenerateScript();
                  else if (activeTab === "script" && script.trim()) onGenerateFromScript();
                }
              }}
              placeholder={
                activeTab === "idea"
                  ? `e.g., ${PLACEHOLDER_EXAMPLES[placeholderIndex]}`
                  : "Enter your full video script here. The AI will generate visuals and audio based on your script..."
              }
              className="w-full px-5 py-4 text-base text-gray-900 placeholder-gray-400 resize-none focus:outline-none bg-transparent"
              rows={activeTab === "script" ? 12 : 4}
              disabled={isLoading}
            />
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
              {activeTab === "script" ? (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">
                    Word count: <span className="font-semibold text-gray-900">{wordCount}</span>
                  </span>
                  {/* <button
                    onClick={handleSurpriseMe}
                    disabled={isLoading || isSurpriseLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSurpriseLoading ? (
                      <>
                        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                        </svg>
                        Surprise Me
                      </>
                    )}
                  </button> */}
                </div>
              ) : (
                <div />
              )}
              <span className="text-sm text-gray-400">
                {activeTab === "idea" ? topic.length : script.length}/{activeTab === "idea" ? "1000" : "10000"}
              </span>
            </div>
          </div>
        </div>

        {/* ── Hook Generator ── */}
        {activeTab === "idea" && (
          <div className="mb-8">
            <button
              onClick={() => setShowHookGenerator(!showHookGenerator)}
              className="flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <span>🎣</span>
              {showHookGenerator ? "Hide Hook Generator" : "Need a hook? Generate viral opening lines"}
              <svg className={`w-4 h-4 transition-transform ${showHookGenerator ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showHookGenerator && (
              <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <p className="text-xs text-gray-500 mb-3">Generate attention-grabbing opening lines. Pick one to prepend to your topic.</p>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={hookTopic}
                    onChange={(e) => setHookTopic(e.target.value)}
                    placeholder="Enter topic for hooks..."
                    className="flex-1 px-3 py-2 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    onKeyDown={(e) => e.key === "Enter" && loadHooks()}
                  />
                  <button
                    onClick={loadHooks}
                    disabled={isLoadingHooks || !hookTopic.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                  >
                    {isLoadingHooks ? "..." : "Generate"}
                  </button>
                </div>
                <div className="flex gap-1.5 mb-3">
                  {[
                    { id: "tiktok", label: "TikTok" },
                    { id: "youtube", label: "YouTube" },
                    { id: "instagram", label: "Instagram" },
                  ].map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setHookPlatform(p.id)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        hookPlatform === p.id ? "bg-primary-100 text-primary-700" : "bg-white text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {hooks.length > 0 && (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {hooks.map((hook, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          const hookText = `${hook.hook} — ${hookTopic}`;
                          onTopicChange(hookText);
                          setShowHookGenerator(false);
                        }}
                        className="w-full text-left p-2.5 bg-white rounded-lg border border-gray-100 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                      >
                        <p className="text-sm font-medium text-gray-900">"{hook.hook}"</p>
                        <p className="text-xs text-gray-400 mt-0.5">{hook.why_it_works}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Trending Topics ── */}
        {activeTab === "idea" && !topic && (
          <div className="mb-6">
            <label className="text-sm font-semibold text-gray-900 mb-2 block">Trending Topics</label>
            <div className="flex gap-2 flex-wrap">
              {TRENDING_TOPICS.map((item) => (
                <button
                  key={item.label}
                  onClick={() => onTopicChange(item.topic)}
                  disabled={isLoading}
                  className="px-3.5 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:bg-primary-100 hover:border-primary-300 transition-all disabled:opacity-50"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Faceless Video Presets ── */}
        {activeTab === "idea" && (
          <div className="mb-6">
            <label className="text-sm font-semibold text-gray-900 mb-2 block">Popular Faceless Formats</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { label: "Reddit Story", topic: "A dramatic Reddit story with a twist ending, told in a suspenseful narration style" },
                { label: "Top 10 Facts", topic: "Top 10 mind-blowing facts about [topic] that most people don't know" },
                { label: "Motivational Quotes", topic: "5 powerful motivational quotes with inspiring visual backgrounds and calm narration" },
                { label: "Scary Stories", topic: "A short creepy horror story with eerie atmosphere and mysterious visuals" },
                { label: "History Explainer", topic: "A fascinating historical event explained in 60 seconds with dramatic visuals" },
                { label: "Did You Know?", topic: "5 surprising 'Did You Know?' facts that will blow your mind" },
              ].map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => onTopicChange(preset.topic)}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Prompt Ideas / Suggestions ── */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-gray-900">Suggestions</label>
            <button
              onClick={loadPromptIdeas}
              disabled={isLoadingIdeas || isLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className={`w-3.5 h-3.5 ${isLoadingIdeas ? "animate-spin" : ""}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
              {isLoadingIdeas ? "Loading..." : "Refresh"}
            </button>
          </div>

          {isLoadingIdeas && promptIdeas.length === 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-56 p-4 bg-white border border-gray-200 rounded-xl animate-pulse">
                  <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-2.5 bg-gray-100 rounded w-full mb-1" />
                  <div className="h-2.5 bg-gray-100 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : promptIdeas.length > 0 ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {promptIdeas.map((idea, index) => (
                <button
                  key={index}
                  onClick={() => handleSelectIdea(idea)}
                  disabled={isLoading}
                  className="flex-shrink-0 w-56 p-4 bg-white border border-gray-200 rounded-xl text-left hover:border-primary-400 hover:bg-primary-50/40 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-2 mb-1.5">
                    <span className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-primary-700 leading-tight">
                      {idea.title}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-2 leading-relaxed">{idea.description}</p>
                  <div className="flex items-center gap-2">
                    {idea.category && (
                      <span className="text-[10px] font-medium text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                        {idea.category}
                      </span>
                    )}
                    {idea.suggested_duration && (
                      <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                        {idea.suggested_duration}s
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* ── Aspect Ratio & Target Duration ── */}
        <div className="flex flex-col sm:flex-row gap-6 sm:gap-10 mb-8">
          {/* Aspect Ratio */}
          <div className="flex-shrink-0">
            <label className="block text-base font-semibold text-gray-900 mb-3">
              Aspect Ratio
            </label>
            <div className="flex gap-3">
              {ASPECT_RATIOS.map((ratio) => (
                <button
                  key={ratio.id}
                  onClick={() => updateSetting("aspectRatio", ratio.id)}
                  disabled={isLoading}
                  className={`w-[90px] sm:w-[105px] p-3 sm:p-4 rounded-xl border-2 transition-all text-center relative ${
                    settings.aspectRatio === ratio.id
                      ? "border-primary-500 bg-white"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  } disabled:opacity-50`}
                >
                  {settings.aspectRatio === ratio.id && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  <div className="flex justify-center mb-2">
                    {ratio.id === "9:16" && (
                      <div className="w-7 h-12 border-2 border-gray-300 rounded-md" />
                    )}
                    {ratio.id === "16:9" && (
                      <div className="w-14 h-9 border-2 border-gray-300 rounded flex items-center justify-center">
                        <span className="text-[9px] text-gray-400 font-bold">16:9</span>
                      </div>
                    )}
                    {ratio.id === "1:1" && (
                      <div className="w-10 h-10 border-2 border-gray-300 rounded flex items-center justify-center">
                        <span className="text-[9px] text-gray-400 font-bold">1:1</span>
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-bold text-gray-900 block">{ratio.label}</span>
                  <span className="text-xs text-gray-500">{ratio.subtitle}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Target Duration - only shown for Idea to Video */}
          {activeTab === "idea" && (
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-3">
                <label className="text-base font-semibold text-gray-900">
                  Target Duration
                </label>
                <span className="text-base font-semibold text-primary-500">
                  {DURATION_STEPS[durationIndex]?.label || "45s"}
                </span>
              </div>
              <div className="pt-6">
                <input
                  type="range"
                  min={0}
                  max={DURATION_STEPS.length - 1}
                  step={1}
                  value={durationIndex}
                  onChange={handleDurationSliderChange}
                  disabled={isLoading}
                  className="duration-slider w-full"
                />
                <div className="flex justify-between mt-2 px-0.5">
                  {DURATION_STEPS.map((step, i) => (
                    <span
                      key={step.value}
                      className={`text-xs cursor-pointer select-none ${
                        i === durationIndex ? "text-primary-500 font-semibold" : "text-gray-400"
                      }`}
                      onClick={() => {
                        if (!isLoading) updateSetting("duration", step.value);
                      }}
                    >
                      {step.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Content Style ── */}
        <div className="mb-8">
          <label className="block text-base font-semibold text-gray-900 mb-3">
            Content Style
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CONTENT_STYLES.map((style) => (
              <button
                key={style.id}
                onClick={() => updateSetting("contentStyle", style.id)}
                disabled={isLoading}
                className={`p-4 rounded-xl border-2 transition-all text-left relative ${
                  settings.contentStyle === style.id
                    ? "border-primary-500 bg-white"
                    : "border-gray-200 bg-white hover:border-gray-300"
                } disabled:opacity-50`}
              >
                {settings.contentStyle === style.id && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <div className={`w-9 h-9 ${style.iconBg} rounded-lg flex items-center justify-center mb-2`}>
                  <StyleIcon styleId={style.id} className={`w-4 h-4 ${style.iconColor}`} />
                </div>
                <span className="text-sm font-medium text-gray-900 block">{style.label}</span>
                <span className="text-xs text-gray-500">{style.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Character Reference (Optional) ── */}
        <div className="mb-8">
          <label className="block text-base font-semibold text-gray-900 mb-3">
            Character Reference (Optional)
          </label>
          
          {!characterImagePreview ? (
            <div
              onClick={() => !isLoading && fileInputRef.current?.click()}
              className={`border-2 border-dashed border-gray-300 rounded-xl p-5 sm:p-8 text-center cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-gray-700">Upload character image</p>
                <p className="text-xs text-gray-400">PNG, JPG up to 10MB</p>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl p-4 bg-white">
              <div className="flex items-center gap-4">
                <img
                  src={characterImagePreview}
                  alt="Character reference"
                  className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                />
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={settings.characterLabel || ""}
                    onChange={(e) => updateSetting("characterLabel", e.target.value)}
                    placeholder="Character name (e.g., Narrator)"
                    disabled={isLoading}
                    className="w-full px-3 py-2 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-50"
                  />
                </div>
                <button
                  onClick={handleRemoveCharacterImage}
                  disabled={isLoading}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleCharacterImageChange}
            className="hidden"
          />
        </div>

        {/* ── Voice Settings ── */}
        <div className="mb-8">
          <label className="block text-base font-semibold text-gray-900 mb-3">
            Voice Settings
          </label>
          <VoiceSelector
            value={settings.voiceId}
            onChange={(voice) => {
              onSettingsChange({
                ...settings,
                voiceId: voice.voice_id,
                voiceName: voice.voice_name,
              });
            }}
            disabled={isLoading}
            inline
          />
        </div>

        {/* ── Advanced Settings Button ── */}
        <div className="mb-8">
          <button
            onClick={() => setShowAdvanced(true)}
            className="w-full group flex items-center justify-between p-5 bg-white border-2 border-gray-200 rounded-xl hover:border-primary-400 hover:bg-primary-50/40 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="text-left">
                <span className="text-base font-semibold text-gray-900 block">Advanced Settings</span>
                <span className="text-sm text-gray-500">Image style, animation style, color theme, subtitles & more</span>
              </div>
            </div>
            <svg className="w-5 h-5 text-gray-400 group-hover:text-primary-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* ── Advanced Settings Modal ── */}
        {showAdvanced && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setShowAdvanced(false)}
            />

            {/* Modal — full-height on mobile, centered on desktop */}
            <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-3xl h-[90vh] sm:h-auto sm:max-h-[85vh] flex flex-col sm:mx-4">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900">Advanced Settings</h2>
                </div>
                <button
                  onClick={() => setShowAdvanced(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6">

                {/* Video Type */}
      

                {/* Image Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How often should images change?
                  </label>
                  <select
                    value={settings.imageFrequency}
                    onChange={(e) => updateSetting("imageFrequency", Number(e.target.value))}
                    disabled={isLoading}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-base"
                  >
                    {IMAGE_FREQUENCIES.map((freq) => (
                      <option key={freq.id} value={freq.id}>{freq.label}</option>
                    ))}
                  </select>
                </div>

                {/* Subtitles */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Include subtitles
                  </label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateSetting("subtitles", true)}
                      disabled={isLoading}
                      className={`px-6 py-2 rounded-lg border-2 transition-all ${
                        settings.subtitles
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-gray-200 bg-white hover:border-gray-300 text-gray-800"
                      } disabled:opacity-50 text-sm font-medium`}
                    >
                      On
                    </button>
                    <button
                      onClick={() => updateSetting("subtitles", false)}
                      disabled={isLoading}
                      className={`px-6 py-2 rounded-lg border-2 transition-all ${
                        !settings.subtitles
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-gray-200 bg-white hover:border-gray-300 text-gray-800"
                      } disabled:opacity-50 text-sm font-medium`}
                    >
                      Off
                    </button>
                  </div>
                </div>

                {/* Image Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Image Style
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4">
                    {IMAGE_STYLES.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => {
                          setSelectedStyleId(style.id);
                          if (style.id === "custom") {
                            setShowCustomStyle(true);
                          } else if (style.id === "default") {
                            setShowCustomStyle(false);
                            updateSetting("imageStyle", "");
                          } else {
                            setShowCustomStyle(false);
                            updateSetting("imageStyle", style.description);
                          }
                        }}
                        disabled={isLoading}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedStyleId === style.id
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        } disabled:opacity-50`}
                      >
                        <span className={`text-sm font-medium block ${
                          selectedStyleId === style.id ? "text-primary-700" : "text-gray-800"
                        }`}>
                          {style.label}
                        </span>
                        <span className="text-xs text-gray-500 line-clamp-2">{style.description}</span>
                      </button>
                    ))}
                  </div>

                  {(showCustomStyle || selectedStyleId === "custom") && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {selectedStyleId === "custom" ? "Custom Style Description" : "Edit Style Description"}
                        </label>
                        {selectedStyleId !== "custom" && (
                          <button
                            onClick={() => setShowCustomStyle(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Hide
                          </button>
                        )}
                      </div>
                      <textarea
                        value={settings.imageStyle || ""}
                        onChange={(e) => updateSetting("imageStyle", e.target.value)}
                        placeholder="e.g., Watercolor painting style with soft pastel colors. No text or logos."
                        disabled={isLoading}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-base resize-none"
                      />
                    </div>
                  )}

                  {settings.imageStyle && selectedStyleId !== "custom" && !showCustomStyle && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Current style:</span>
                        <button
                          onClick={() => setShowCustomStyle(true)}
                          className="text-xs text-primary-600 hover:text-primary-700"
                        >
                          Edit
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{settings.imageStyle}</p>
                    </div>
                  )}
                </div>

                {/* Theme Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Theme Color Palette
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose a color palette for consistent visuals across all images
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                    {THEME_COLORS.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => {
                          setSelectedThemeColorId(theme.id);
                          if (theme.id === "none") {
                            updateSetting("themeColor", "");
                          } else if (theme.id === "custom") {
                            // Keep existing custom color or empty
                          } else {
                            updateSetting("themeColor", theme.description);
                          }
                        }}
                        disabled={isLoading}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          selectedThemeColorId === theme.id
                            ? "border-primary-500 bg-primary-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        } disabled:opacity-50`}
                      >
                        {theme.colors.length > 0 ? (
                          <div className="flex justify-center gap-1 mb-2">
                            {theme.colors.map((color, i) => (
                              <div
                                key={i}
                                className="w-4 h-4 rounded-full border border-gray-200"
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="flex justify-center gap-1 mb-2">
                            <div className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-200 to-gray-400 border border-gray-200" />
                          </div>
                        )}
                        <span className={`text-xs font-medium block ${
                          selectedThemeColorId === theme.id ? "text-primary-700" : "text-gray-700"
                        }`}>
                          {theme.label}
                        </span>
                      </button>
                    ))}
                  </div>

                  {selectedThemeColorId === "custom" && (
                    <div className="mt-3">
                      <input
                        type="text"
                        value={customThemeColor}
                        onChange={(e) => {
                          setCustomThemeColor(e.target.value);
                          updateSetting("themeColor", e.target.value);
                        }}
                        placeholder="e.g., Soft blues and greens, ocean-inspired palette"
                        disabled={isLoading}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 text-base"
                      />
                    </div>
                  )}

                  {settings.themeColor && selectedThemeColorId !== "custom" && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-xs text-gray-500">Selected palette: </span>
                      <span className="text-sm text-gray-700">{settings.themeColor}</span>
                    </div>
                  )}
                </div>

                {/* Video/Animation Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Animation Style
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose how animated scenes should move and transition
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mb-4">
                    {VIDEO_STYLES.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => {
                          setSelectedVideoStyleId(style.id);
                          if (style.id === "custom") {
                            setShowCustomVideoStyle(true);
                          } else if (style.id === "default") {
                            setShowCustomVideoStyle(false);
                            updateSetting("videoStyle", "");
                          } else {
                            setShowCustomVideoStyle(false);
                            updateSetting("videoStyle", style.description);
                          }
                        }}
                        disabled={isLoading}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedVideoStyleId === style.id
                            ? "border-purple-500 bg-purple-50"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        } disabled:opacity-50`}
                      >
                        <span className={`text-sm font-medium block ${
                          selectedVideoStyleId === style.id ? "text-purple-700" : "text-gray-800"
                        }`}>
                          {style.label}
                        </span>
                        <span className="text-xs text-gray-500 line-clamp-2">{style.description}</span>
                      </button>
                    ))}
                  </div>

                  {(showCustomVideoStyle || selectedVideoStyleId === "custom") && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {selectedVideoStyleId === "custom" ? "Custom Animation Style" : "Edit Animation Style"}
                        </label>
                        {selectedVideoStyleId !== "custom" && (
                          <button
                            onClick={() => setShowCustomVideoStyle(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Hide
                          </button>
                        )}
                      </div>
                      <textarea
                        value={settings.videoStyle || ""}
                        onChange={(e) => updateSetting("videoStyle", e.target.value)}
                        placeholder="e.g., Slow cinematic zoom with gentle parallax effect and floating particles"
                        disabled={isLoading}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 text-base resize-none"
                      />
                    </div>
                  )}

                  {settings.videoStyle && selectedVideoStyleId !== "custom" && !showCustomVideoStyle && (
                    <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Animation style:</span>
                        <button
                          onClick={() => setShowCustomVideoStyle(true)}
                          className="text-xs text-purple-600 hover:text-purple-700"
                        >
                          Edit
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{settings.videoStyle}</p>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <button
                  onClick={() => setShowAdvanced(false)}
                  className="px-6 py-2.5 bg-primary-500 text-white font-medium rounded-xl hover:bg-primary-600 transition-colors text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Animate Visuals ── */}
        {/* <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg" aria-hidden="true">✨</span>
                <h2 className="text-base font-semibold text-gray-900">Animate Visuals</h2>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Transform static images into animated clips using AI video generation.
              </p>
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                <span>⚠️</span>
                <span>This increases generation time and cost</span>
              </div>
            </div>
            <div className="ml-4">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => updateSetting("animateVisuals", true)}
                  disabled={isLoading}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    settings.animateVisuals
                      ? "bg-primary-500 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  } disabled:opacity-50`}
                >
                  ON
                </button>
                <button
                  onClick={() => updateSetting("animateVisuals", false)}
                  disabled={isLoading}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    !settings.animateVisuals
                      ? "bg-gray-100 text-gray-900"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  } disabled:opacity-50`}
                >
                  OFF
                </button>
              </div>
            </div>
          </div>

          {settings.animateVisuals && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Animation Scope
              </label>
              <div className="space-y-2">
                {ANIMATION_SCOPES.map((scope) => (
                  <label
                    key={scope.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      settings.animationScope === scope.id
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:border-gray-300"
                    } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <input
                      type="radio"
                      name="animationScope"
                      value={scope.id}
                      checked={settings.animationScope === scope.id}
                      onChange={() => updateSetting("animationScope", scope.id)}
                      disabled={isLoading}
                      className="w-4 h-4 text-primary-500 focus:ring-primary-500"
                    />
                    <div>
                      <span className={`text-sm font-medium ${
                        settings.animationScope === scope.id ? "text-primary-700" : "text-gray-800"
                      }`}>
                        {scope.label}
                      </span>
                      <span className="text-xs text-gray-500 ml-2">{scope.description}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div> */}

      </div>

      {/* ── Sticky Bottom Bar ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 sm:gap-8 min-w-0">
            <div className="flex-shrink-0">
              <p className="text-xs text-gray-500 mb-0.5">Estimated Cost</p>
              <p className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-1.5">
                <span>🪙</span> {estimate.total} Credits
              </p>
            </div>
            <div className="hidden sm:flex flex-col gap-1">
              <span className="text-xs text-gray-600 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block flex-shrink-0" />
                Script Generation: {estimate.breakdown.audio.cost} credits
              </span>
              <span className="text-xs text-gray-600 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block flex-shrink-0" />
                Video Production: {estimate.breakdown.images.cost + estimate.breakdown.animations.cost + estimate.breakdown.subtitles.cost} credits
              </span>
            </div>
          </div>

          <button
            onClick={activeTab === "idea" ? onGenerateScript : onGenerateFromScript}
            disabled={activeTab === "idea" ? (!topic.trim() || isLoading) : (!script.trim() || isLoading)}
            className="px-5 sm:px-8 py-3 sm:py-3.5 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 hover:scale-[1.02] active:scale-[0.98] flex-shrink-0 text-sm sm:text-base"
          >
            {isLoading ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {activeTab === "idea" ? "Generating Script..." : "Generating Assets..."}
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01" />
                </svg>
                {activeTab === "idea" ? "Generate Script" : "Generate Assets"}
                <kbd className="hidden sm:inline-flex ml-1 items-center px-1.5 py-0.5 text-[10px] font-medium bg-white/20 rounded">
                  {navigator.platform?.includes("Mac") ? "⌘" : "Ctrl"}↵
                </kbd>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
